// Api
import homeApi from './api';

// Action Types
import {
  FETCH_MAP_LAYERS_BY_PROJ_CAT_TREE, FETCH_PROJECT_CATEGORIES,
  FETCH_KML_BY_SOURCE_BOUNDINGBOX_ZOOM, MAP_VIEWPORT_PROPERTIES,
  SELECT_FEATURE, FETCH_MAP_LAYERS_BY_LAYER, FETCH_MAP_LAYERS_BY_LAYER_SEARCH,
  SEARCH_TEXT, MAP_LAYER, FEATURE_DESCRIPTION
} from './actionTypes';

// Services
import loggerService from '../shared/services/logger';

// Utils
import { groupCategoriesByProject, extractProjectCategoryFromCSS } from './utils/projectCategory';
import { convertKmlDataLayerToJson } from './utils/layer';
import { isFirstRender } from '../shared/utils/data';


export const fetchMapLayersByProjCatTree = ( token, query ) => (dispatch, getState) => {
  const requestFetchMapLayersByProjCatTree = () => ({
    type: FETCH_MAP_LAYERS_BY_PROJ_CAT_TREE.request()
  });

  const receivedMapLayersByProjCatTree = payload => ({
    type: FETCH_MAP_LAYERS_BY_PROJ_CAT_TREE.success(),
    payload
  });

  const errorOnFetchMapLayersByProjCatTree = error => ({
    type: FETCH_MAP_LAYERS_BY_PROJ_CAT_TREE.error(),
    payload: error
  });

  dispatch(requestFetchMapLayersByProjCatTree());

  return homeApi.fetchMapLayersByProjCatTree(token, query)
    .then(({ data: mapLayersByProjCatTree }) => dispatch(receivedMapLayersByProjCatTree({
      mapLayersByProjCatTree,
      projectCategoryFromCSS: extractProjectCategoryFromCSS(
        mapLayersByProjCatTree,
        getState().authentication.profile.data.title
      )
    })))
    .catch(error => dispatch(errorOnFetchMapLayersByProjCatTree(error)));
};

export const fetchKMLBySourceBoundingBoxZoom = (
  token, mapLayer, mapViewportProperties, userLocation = null, query = undefined
) => dispatch => {
  const requestFetchKMLBySourceBoundingBoxZoom = () => ({
    type: FETCH_KML_BY_SOURCE_BOUNDINGBOX_ZOOM.request()
  });

  const receivedKMLBySourceBoundingBoxZoom = layer => ({
    type: FETCH_KML_BY_SOURCE_BOUNDINGBOX_ZOOM.success(),
    payload: layer
  });

  const errorOnFetchKMLBySourceBoundingBoxZoom = error => ({
    type: FETCH_KML_BY_SOURCE_BOUNDINGBOX_ZOOM.error(),
    payload: error
  });

  const { bounds, zoom } = mapViewportProperties;
  const params = {
    name: mapLayer.name,
    organizationId: mapLayer.organizationId,
    geocodeLayerId: mapLayer.geocodeLayerId,
    boundingBox: {
      north: bounds.ne.lat,
      west: bounds.nw.lng,
      south: bounds.se.lat,
      east: bounds.sw.lng
    },
    currentZoom: zoom,
    maxZoom: 25
  };

  dispatch(requestFetchKMLBySourceBoundingBoxZoom());

  return homeApi.fetchKMLBySourceBoundingBoxZoom(token, params, query)
    .then(kmlData => dispatch(receivedKMLBySourceBoundingBoxZoom({
      mapLayer,
      dataLayer: convertKmlDataLayerToJson(kmlData, userLocation)
    })))
    .catch(error => dispatch(errorOnFetchKMLBySourceBoundingBoxZoom(error)));
};

export const fetchProjectCategories = ( token, zoneId ) => dispatch => {
  const requestFetchProjectCategories = () => ({
    type: FETCH_PROJECT_CATEGORIES.request()
  });

  const receivedProjectCategories = projectCategories => ({
    type: FETCH_PROJECT_CATEGORIES.success(),
    payload: projectCategories
  });

  const errorOnFetchProjectCategories = error => ({
    type: FETCH_PROJECT_CATEGORIES.error(),
    payload: error
  });

  dispatch(requestFetchProjectCategories());

  return homeApi.fetchProjectCategories(token).then(({ data }) => dispatch(
    receivedProjectCategories(groupCategoriesByProject(data, zoneId))
  )).catch(error => dispatch(errorOnFetchProjectCategories(error)));
};

export const updateMapViewportProperties = ( viewportProperties ) => dispatch => {
  const receivedMapViewportProperties = viewportProperties => ({
    type: MAP_VIEWPORT_PROPERTIES.update(),
    payload: viewportProperties
  });

  dispatch(receivedMapViewportProperties( viewportProperties ));
};

export const selectFeature = (token, profile, mapLayer, feature) => dispatch => {
  const receivedSelectedFeature = feature => ({
    type: SELECT_FEATURE.success(),
    payload: feature
  });

  const msg = `customer_clicked_marker=${loggerService.markerOnReportBuilder(profile, mapLayer, feature)}`;

  loggerService.audit(token, msg);
  dispatch(receivedSelectedFeature( feature ));
};

export const fetchMapLayersByLayer = (token, username, layerId, query) => dispatch => {
  const requestFetchMapLayers = () => ({
    type: FETCH_MAP_LAYERS_BY_LAYER.request()
  });

  const receivedFetchMapLayers = (mapLayers) => ({
    type: FETCH_MAP_LAYERS_BY_LAYER.success(),
    payload: mapLayers
  });

  const errorOnFetchMapLayers = error => ({
    type: FETCH_MAP_LAYERS_BY_LAYER.error(),
    payload: error
  });

  const { filter } = query;

  dispatch(requestFetchMapLayers());

  return homeApi.fetchMapLayersByLayer(token, username, { layerId }, query)
    .then(({ layers }) => dispatch(receivedFetchMapLayers(
      // The response will have the reference to the KML layer generated for the user
      { filter, mapLayer: isFirstRender(layers) ? null : layers[0] }
    )))
    .catch(error => dispatch(errorOnFetchMapLayers(error)));
};

export const fetchMapLayersByLayerSearch = (token, layerId, query) => dispatch => {
  const requestFetchMapLayersSearch = () => ({
    type: FETCH_MAP_LAYERS_BY_LAYER_SEARCH.request()
  });

  const receivedMatchedResponses = (matchedResponses) => ({
    type: FETCH_MAP_LAYERS_BY_LAYER_SEARCH.success(),
    payload: matchedResponses
  });

  const errorOnFetchMapLayersSearch = error => ({
    type: FETCH_MAP_LAYERS_BY_LAYER_SEARCH.error(),
    payload: error
  });

  dispatch(requestFetchMapLayersSearch());

  return homeApi.fetchMapLayersByLayerSearch(token, { layerId }, query)
    .then(matchedResponses => dispatch(receivedMatchedResponses(matchedResponses)))
    .catch(error => dispatch(errorOnFetchMapLayersSearch(error)));
};

export const clearSearchText = () => dispatch => {
  const clearSearchText = () => ({
    type: SEARCH_TEXT.clear()
  });

  dispatch(clearSearchText());
};

export const selectMapLayer = (token, profile, mapLayer) => dispatch => {
  const selectMapLayer = (mapLayer) => ({
    type: MAP_LAYER.select(),
    payload: mapLayer
  });

  const msg = `customer_activated_layer=${loggerService.layerOnReportBuilder(profile, mapLayer)}`;

  loggerService.audit(token, msg);
  dispatch(selectMapLayer( mapLayer ));
};

export const toggleFeatureDescription = () => dispatch => {
  const toggleFeatureDesc = () => ({
    type: FEATURE_DESCRIPTION.toggle()
  });

  dispatch(toggleFeatureDesc());
};
